import type {
  Colaborador,
  ConfiguracionGlobal,
  EdicionPrograma,
  Evento,
  Galeria,
  Noticia,
  PaginaEstatica,
  PaginaInicio,
  PaginaProyecto,
  Persona,
  Programa,
  Sede,
  StrapiListResponse,
  StrapiResponse,
} from "../types/cms";

const CMS_URL = (import.meta.env.VITE_CMS_URL ?? "http://localhost:1337").replace(/\/$/, "");

type Query = Record<string, string | number | boolean | undefined>;

function buildQuery(params: Query = {}): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) usp.append(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
}

async function strapiFetch<T>(path: string, params?: Query): Promise<T> {
  const url = `${CMS_URL}/api${path}${buildQuery(params)}`;
  const res = await fetch(url, { headers: { "Content-Type": "application/json" } });
  if (!res.ok) {
    throw new Error(`Strapi ${res.status} en ${path}: ${await res.text()}`);
  }
  return (await res.json()) as T;
}

/** URL absoluta a un media de Strapi (las relativas vienen como /uploads/...). */
export function mediaUrl(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("http")) return url;
  return `${CMS_URL}${url}`;
}

// --- Programa ---
export async function fetchProgramas() {
  return strapiFetch<StrapiListResponse<Programa>>("/programas", {
    "populate[imagenPortada]": "true",
    "sort": "ordenListado:asc",
  });
}

export async function fetchProgramaBySlug(slug: string) {
  return strapiFetch<StrapiListResponse<Programa>>("/programas", {
    "filters[slug][$eq]": slug,
    "populate[imagenPortada]": "true",
    "populate[galeriaPortada]": "true",
    "populate[ediciones][populate][sedes]": "true",
    "populate[ediciones][populate][monitores]": "true",
    "populate[ediciones][populate][coordinador]": "true",
    "populate[ediciones][populate][imagenPortada]": "true",
  });
}

// --- Edicion programa ---
export async function fetchEdicion(slug: string) {
  return strapiFetch<StrapiListResponse<EdicionPrograma>>("/ediciones-programa", {
    "filters[slug][$eq]": slug,
    "populate[programa]": "true",
    "populate[sedes]": "true",
    "populate[monitores][populate][foto]": "true",
    "populate[coordinador][populate][foto]": "true",
    "populate[galeria]": "true",
    "populate[documentoMatricula]": "true",
    "populate[imagenPortada]": "true",
  });
}

// --- Sede ---
export async function fetchSedes() {
  return strapiFetch<StrapiListResponse<Sede>>("/sedes", {
    "populate[imagenPortada]": "true",
    "populate[colaboradorPrincipal][populate][logo]": "true",
    "sort": "ciudad:asc",
  });
}

// --- Persona ---
// IMPORTANTE: el backend debe filtrar `foto` cuando consentimientoRgpd=false.
// Aquí el frontend pide foto y muestra avatar genérico si llega null.
export async function fetchPersonas(filters?: { equipoEstable?: boolean }) {
  const params: Query = {
    "populate[foto]": "true",
    "sort": "nombre:asc",
  };
  if (filters?.equipoEstable !== undefined) {
    params["filters[equipoEstable][$eq]"] = filters.equipoEstable;
  }
  return strapiFetch<StrapiListResponse<Persona>>("/personas", params);
}

// --- Galería ---
export async function fetchGalerias(limit = 12) {
  return strapiFetch<StrapiListResponse<Galeria>>("/galerias", {
    "populate[fotos]": "true",
    "populate[portada]": "true",
    "sort": "fecha:desc",
    "pagination[pageSize]": limit,
  });
}

export async function fetchGaleriaBySlug(slug: string) {
  return strapiFetch<StrapiListResponse<Galeria>>("/galerias", {
    "filters[slug][$eq]": slug,
    "populate[fotos]": "true",
    "populate[portada]": "true",
    "populate[edicion][populate][programa]": "true",
    "populate[evento]": "true",
  });
}

// --- Noticia ---
export async function fetchNoticias(limit = 12) {
  return strapiFetch<StrapiListResponse<Noticia>>("/noticias", {
    "populate[imagenPortada]": "true",
    "populate[autor]": "true",
    "sort": "fechaPublicacion:desc",
    "pagination[pageSize]": limit,
  });
}

/**
 * Sólo las noticias marcadas como destacadas, ordenadas por fecha desc.
 * Para el bloque de la home — el equipo marca destacada=true en las
 * noticias que quiera resaltar. Si quieres rotar, desmarca la vieja
 * antes de marcar la nueva (lo ideal: 1-3 destacadas a la vez).
 */
export async function fetchNoticiasDestacadas(limit = 3) {
  return strapiFetch<StrapiListResponse<Noticia>>("/noticias", {
    "populate[imagenPortada]": "true",
    "filters[destacada][$eq]": true,
    "sort": "fechaPublicacion:desc",
    "pagination[pageSize]": limit,
  });
}

export async function fetchNoticiaBySlug(slug: string) {
  return strapiFetch<StrapiListResponse<Noticia>>("/noticias", {
    "filters[slug][$eq]": slug,
    "populate[imagenPortada]": "true",
    "populate[autor][populate][foto]": "true",
  });
}

// --- Evento ---
export async function fetchEventosProximos(limit = 6) {
  const today = new Date().toISOString();
  return strapiFetch<StrapiListResponse<Evento>>("/eventos", {
    "populate[imagenPortada]": "true",
    "populate[sede]": "true",
    "filters[fechaInicio][$gte]": today,
    "sort": "fechaInicio:asc",
    "pagination[pageSize]": limit,
  });
}

export async function fetchEventos(limit = 48) {
  return strapiFetch<StrapiListResponse<Evento>>("/eventos", {
    "populate[imagenPortada]": "true",
    "populate[sede]": "true",
    "sort": "fechaInicio:desc",
    "pagination[pageSize]": limit,
  });
}

export async function fetchEventoBySlug(slug: string) {
  return strapiFetch<StrapiListResponse<Evento>>("/eventos", {
    "filters[slug][$eq]": slug,
    "populate[imagenPortada]": "true",
    "populate[sede]": "true",
    "populate[programa]": "true",
    "populate[galeria][populate][portada]": "true",
  });
}

// --- Colaborador / Patrocinador ---
// Lista todos los colaboradores publicados, ordenados por el campo `orden`
// (ascendente: el menor número aparece primero — útil para fijar la posición
// de los logos institucionales más relevantes).
export async function fetchColaboradores() {
  return strapiFetch<StrapiListResponse<Colaborador>>("/colaboradores", {
    "populate[logo]": "true",
    "sort": "orden:asc",
  });
}

// --- Página estática ---
export async function fetchPaginaEstatica(slug: string) {
  return strapiFetch<StrapiListResponse<PaginaEstatica>>("/paginas-estaticas", {
    "filters[slug][$eq]": slug,
  });
}

// --- Single types ---
export async function fetchConfiguracionGlobal() {
  return strapiFetch<StrapiResponse<ConfiguracionGlobal>>("/configuracion-global");
}

export async function fetchPaginaProyecto() {
  return strapiFetch<StrapiResponse<PaginaProyecto>>("/pagina-proyecto", {
    "populate[galeriaImagenes]": "true",
  });
}

export async function fetchPaginaInicio() {
  return strapiFetch<StrapiResponse<PaginaInicio>>("/pagina-inicio", {
    "populate[heroCtaPrimario]": "true",
    "populate[heroCtaSecundario]": "true",
    "populate[pilares]": "true",
    "populate[programasDestacados][populate][imagenPortada]": "true",
    "populate[bloqueCtaFinal][populate][cta1]": "true",
    "populate[bloqueCtaFinal][populate][cta2]": "true",
  });
}
